import { useState } from "react";
import { FaTelegram } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import { FiChevronDown, FiClipboard, FiLink, FiMessageCircle, FiShield } from "react-icons/fi";
import { PRIVACY_POLICY_URL } from "@/lib/utils";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Header */}
      <header className="py-4 md:py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center">
          <Logo />
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-4 py-4 md:py-12 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-700 mb-3 md:mb-4">
          Matching Schools with the{" "}
          <span className="text-accent-red">right relief teachers</span>
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
          Quickly find and connect with the right relief teachers through
          Telegram.
        </p>
      </section>

      {/* Bot Links Section */}
      <section className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        <h2 className="text-xl md:text-2xl font-semibold text-center text-primary-700 mb-4 md:mb-8">
          Get Started
        </h2>
        <div className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-2xl mx-auto">
          {/* Teacher Bot Card */}
          <BotCard
            title="For Teachers"
            description="Register your profile and get notified when matching opportunities appear."
            botLink="https://go.gov.sg/matcher-teachers"
            buttonText="Join as Teacher"
            qrCodeSrc="/qr-teachers.png"
            accentColor="border-t-accent-red"
          />

          {/* School Bot Card */}
          <BotCard
            title="For Schools"
            description="Post your requirements and get matched with qualified relief teachers."
            botLink="https://go.gov.sg/matcher-schools"
            buttonText="Join as School"
            qrCodeSrc="/qr-schools.png"
            accentColor="border-t-primary-500"
          />
        </div>
        <div className="text-xs text-gray-500 text-center mt-4 space-y-1">
          <p className="flex items-center justify-center gap-1.5">
            <FiShield className="text-sm flex-shrink-0" />
            Your data is only used for matching.{" "}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-600"
            >
              Privacy Policy
            </a>
          </p>
          <p className="flex items-center justify-center gap-1.5">
            <HiOutlineMail className="text-sm flex-shrink-0" />
            Questions? Reach us at{" "}
            <a
              href="mailto:matcher@hack2026.gov.sg"
              className="text-primary-600 hover:text-primary-700 underline underline-offset-2"
            >
              matcher@hack2026.gov.sg
            </a>
          </p>
        </div>
      </section>

      {/* What to Expect Section - collapsible on mobile */}
      <WhatToExpectSection />

      {/* FAQ Section */}
      <FaqSection />


    </div>
  );
}

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <img src="/logo.svg" alt="Matcher logo" width={40} height={34} className="w-8 h-7 md:w-10 md:h-[34px]" />
      <span
        className="text-2xl md:text-3xl font-semibold"
        style={{ fontFamily: "var(--font-lexend), 'Lexend', sans-serif" }}
      >
        <span className="text-primary-700">mat</span>
        <span className="text-accent-red">cher</span>
      </span>
    </div>
  );
}

function BotCard({
  title,
  description,
  botLink,
  buttonText,
  qrCodeSrc,
  accentColor,
}: {
  title: string;
  description: string;
  botLink: string;
  buttonText: string;
  qrCodeSrc?: string;
  accentColor?: string;
}) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow border-t-4 ${accentColor ?? ""}`}>
      <h3 className="text-lg md:text-xl font-semibold text-primary-700 mb-2 md:mb-3">{title}</h3>
      <p className="text-gray-600 mb-4 md:mb-6 text-sm leading-relaxed">
        {description}
      </p>
      <a
        href={botLink}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 bg-[#0088cc] hover:bg-[#0077b5] text-white font-medium py-3 px-5 md:px-6 rounded-lg transition-colors w-full justify-center text-sm md:text-base"
      >
        <FaTelegram className="text-lg md:text-xl" />
        {buttonText}
      </a>
      <p className="text-xs text-gray-500 mt-2 text-center hidden md:block">
        via go.gov.sg
      </p>
      {qrCodeSrc && (
        <div className="hidden md:flex mt-4 flex-col items-center">
          <a href={botLink} target="_blank" rel="noopener noreferrer">
            <img
              src={qrCodeSrc}
              alt="Scan QR code"
              width={160}
              height={160}
              className="hover:opacity-80 transition-opacity"
            />
          </a>
          <span className="text-xs text-gray-400 mt-2">
            or scan with your phone
          </span>
        </div>
      )}
    </div>
  );
}

const STEPS = [
  {
    title: "Share your profile",
    description: "Tell us your subjects, levels, and availability via a quick FormSG form.",
    icon: <FiClipboard className="text-xl" />,
  },
  {
    title: "We find your matches",
    description: "Our system automatically matches teachers with schools based on your criteria.",
    icon: <FiLink className="text-xl" />,
  },
  {
    title: "Connect via Telegram",
    description: "Get notified instantly and reach out to each other directly.",
    icon: <FiMessageCircle className="text-xl" />,
  },
];

function WhatToExpectSection() {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="bg-gray-50 py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Always visible on desktop, toggle on mobile */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-2 md:pointer-events-none"
        >
          <h2 className="text-xl md:text-2xl font-semibold text-center text-primary-700">
            What to expect
          </h2>
          <FiChevronDown
            className={`text-primary-700 text-xl md:hidden transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Desktop: always visible. Mobile: collapsible */}
        <div
          className={`overflow-hidden transition-all duration-300 md:!max-h-none md:!opacity-100 md:!mt-8 ${
            expanded ? "max-h-96 opacity-100 mt-6" : "max-h-0 opacity-0 mt-0"
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 sm:gap-8">
            {STEPS.map((step, i) => (
              <div key={i} className="flex sm:flex-col items-center sm:items-center">
                <div className="flex sm:flex-col items-center gap-4 sm:gap-0 w-full py-3 sm:py-0">
                  <div className="relative flex flex-col items-center flex-shrink-0">
                    {i > 0 && (
                      <div className="absolute bottom-full w-px h-3 bg-primary-200 sm:hidden" />
                    )}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-500 text-white rounded-full flex items-center justify-center sm:mb-3">
                      {step.icon}
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="absolute top-full w-px h-3 bg-primary-200 sm:hidden" />
                    )}
                  </div>
                  <div className="text-left sm:text-center">
                    <h3 className="text-base sm:text-lg font-semibold text-primary-700 mb-1 sm:mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

type FaqCategory = "general" | "teachers" | "schools";

const FAQ_DATA: { category: FaqCategory; label: string; items: { q: string; a: React.ReactNode }[] }[] = [
  {
    category: "general",
    label: "General",
    items: [
      { q: "Is this free?", a: "Yes, completely free to use." },
      { q: "What happens to my data?", a: "Only used for matching within this pilot. We don't share it with third parties." },
      { q: "Is this an official MOE thing?", a: "matCHER is a prototype by Open Government Products (part of GovTech), piloted in collaboration with MOE." },
      { q: "What happens after the pilot ends?", a: "We'll gather feedback, assess usefulness, and decide on next steps. We'll keep you posted either way." },
      {
        q: "Who do I contact if I have issues?",
        a: <>Reach us at <a href="mailto:matcher@hack2026.gov.sg" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">matcher@hack2026.gov.sg</a>. We&apos;re a small team but we&apos;ll get back to you.</>,
      },
    ],
  },
  {
    category: "teachers",
    label: "For Teachers",
    items: [
      { q: "How do I get matched?", a: "Submit your profile (subjects, levels, availability) and we'll automatically match you with school vacancies. Both sides get notified when there's a fit." },
      { q: "Will I be notified of every vacancy?", a: "Only relevant ones that match your profile. No spam." },
      { q: "Can I update my preferences?", a: "Yes. You can change your subjects, levels, and availability anytime." },
      { q: "What if there are no matches when I sign up?", a: "Your profile is saved and you'll be notified via Telegram the moment a matching role appears. We're actively onboarding more schools." },
      {
        q: "Can I pause or opt out?",
        a: <>Yes. Update your availability to stop receiving opportunities. To opt out entirely, email <a href="mailto:matcher@hack2026.gov.sg" className="text-primary-600 hover:text-primary-700 underline underline-offset-2">matcher@hack2026.gov.sg</a>.</>,
      },
    ],
  },
  {
    category: "schools",
    label: "For Schools",
    items: [
      { q: "How are teachers verified?", a: "We've reached out only to teachers from MOE's registered pool of relief educators (on REMS)." },
      { q: "Can we still use our existing channels?", a: "Absolutely. matCHER complements your existing sourcing methods." },
      { q: "How fast can we expect candidates?", a: "Depends on teacher availability. The goal is to surface relevant matches faster than starting from scratch." },
      { q: "What if we don't find a suitable match?", a: "Your request stays in our system and you'll be notified via Telegram when a suitable match appears. We're actively growing our teacher pool." },
      { q: "Do you stay involved after we connect?", a: "No. We surface the match — you reach out directly via the bot. The usual REMS process applies for hiring. We may check in for feedback." },
    ],
  },
];

function FaqSection() {
  const [activeCategory, setActiveCategory] = useState<FaqCategory>("general");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const activeGroup = FAQ_DATA.find((g) => g.category === activeCategory)!;

  return (
    <section className="max-w-3xl mx-auto px-4 py-8 md:py-12">
      <h2 className="text-xl md:text-2xl font-semibold text-center text-primary-700 mb-6 md:mb-8">
        Frequently Asked Questions
      </h2>

      {/* Category tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-6 md:mb-8">
        {FAQ_DATA.map((group) => (
          <button
            key={group.category}
            onClick={() => {
              setActiveCategory(group.category);
              setOpenIndex(null);
            }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === group.category
                ? "bg-primary-700 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {group.label}
          </button>
        ))}
      </div>

      {/* FAQ items */}
      <div className="divide-y divide-gray-200 border-t border-gray-200">
        {activeGroup.items.map((item, i) => (
          <FaqItem
            key={`${activeCategory}-${i}`}
            question={item.q}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
          >
            {item.a}
          </FaqItem>
        ))}
      </div>
    </section>
  );
}

function FaqItem({
  question,
  children,
  isOpen,
  onToggle,
}: {
  question: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-4 md:py-5 text-left group"
      >
        <span className="text-sm md:text-base font-medium text-gray-800 group-hover:text-primary-700 transition-colors">
          {question}
        </span>
        <span
          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full transition-all duration-200 ${
            isOpen
              ? "bg-primary-700 text-white rotate-45"
              : "bg-gray-100 text-gray-400 group-hover:bg-gray-200"
          }`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="6" y1="2" x2="6" y2="10" />
            <line x1="2" y1="6" x2="10" y2="6" />
          </svg>
        </span>
      </button>
      <div className="faq-grid" data-open={isOpen}>
        <div>
          <p className="pb-4 md:pb-5 pr-10 text-sm text-gray-600 leading-relaxed">
            {children}
          </p>
        </div>
      </div>
    </div>
  );
}
